import { Controller } from "@hotwired/stimulus"

// Multi-row selection + partial/all-in-filter banner + right-click action popup
// for Cup::DataTable::Component. Selection state is in-memory only (resets on
// reload/paginate/filter — intentional).
//
// The popup (spec: docs/specs/overviews/_settings.md §Popup) is enabled only
// when the table declares row_actions. Rows carry data-row-id + data-actions
// ("key1,key2"); the controller shows the intersection of actions across the
// target set (the selection, or the clicked row when nothing is selected),
// then POSTs the chosen action's endpoint with ids[] (one request, Turbo
// reloads the index).
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "banner", "bannerCount",
                    "bannerPartial", "bannerAllInFilter",
                    "popup", "popupHeading", "popupItems",
                    "confirmDialog", "confirmMessage", "confirmLabel"]
  static values = {
    total: Number,
    pageSize: Number,
    noun: { type: String, default: "" },
    actionLabels: { type: Object, default: {} },
    actionOrder: { type: Array, default: [] },
    actionEndpoints: { type: Object, default: {} },
    actionConfirms: { type: Object, default: {} },
    actionConfirmLabels: { type: Object, default: {} },
    confirmFallbackLabel: { type: String, default: "" },
    singleOnlyActions: { type: Array, default: [] },
    actionSubmenus: { type: Object, default: {} },
    emptyHeading: { type: String, default: "" },
    emptySelectionHeading: { type: String, default: "" }
  }

  connect() {
    this.allInFilter = false
    this.onDocClick = this.onDocClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKeydown)
    this.updateState()
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  // ---------- selection ----------

  toggleRow() {
    this.allInFilter = false
    this.updateState()
  }

  toggleAll(event) {
    const checked = event.currentTarget.checked
    this.checkboxTargets.forEach(cb => { cb.checked = checked })
    this.allInFilter = false
    this.updateState()
  }

  selectAllInFilter(event) {
    event.preventDefault()
    this.allInFilter = true
    this.updateState()
  }

  clearSelection(event) {
    event.preventDefault()
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.allInFilter = false
    this.updateState()
  }

  selectedCount() {
    return this.checkboxTargets.filter(cb => cb.checked).length
  }

  selectedRows() {
    return this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.closest("tr")).filter(Boolean)
  }

  updateState() {
    const selected = this.selectedCount()
    const rows = this.checkboxTargets.length
    const allOnPageSelected = rows > 0 && this.checkboxTargets.every(cb => cb.checked)

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = allOnPageSelected
      this.selectAllTarget.indeterminate = !allOnPageSelected && selected > 0
    }

    if (!this.hasBannerTarget) return

    if (selected === 0) {
      this.banner(false)
      return
    }

    this.banner(true)
    // Two states: partial (page selection, blue) vs the whole filter set (amber).
    // The total is server-rendered into each message; only the page count is dynamic.
    if (this.hasBannerCountTarget) {
      this.bannerCountTarget.textContent = this.formatNumber(selected)
    }
    if (this.hasBannerPartialTarget) this.bannerPartialTarget.hidden = this.allInFilter
    if (this.hasBannerAllInFilterTarget) this.bannerAllInFilterTarget.hidden = !this.allInFilter
  }

  banner(show) {
    if (show) this.bannerTarget.removeAttribute("hidden")
    else this.bannerTarget.setAttribute("hidden", "hidden")
  }

  formatNumber(n) {
    return Number(n).toLocaleString()
  }

  // ---------- right-click action popup ----------

  showPopup(event) {
    if (!this.hasPopupTarget) return
    event.preventDefault()

    let targetRows = this.selectedRows()
    let count
    if (targetRows.length === 0) {
      targetRows = [event.currentTarget]
      count = 1
    } else {
      count = this.allInFilter ? this.totalValue : targetRows.length
    }

    const actions = this.intersectActions(targetRows)
    this.menuRows = targetRows
    this.menuActions = actions
    this.menuCount = count
    this.renderPopupItems(actions, targetRows)
    this.setPopupHeading(count, actions.length)

    const popup = this.popupTarget
    popup.classList.remove("hidden")
    const margin = 4
    const x = Math.min(event.clientX, window.innerWidth - popup.offsetWidth - margin)
    const y = Math.min(event.clientY, window.innerHeight - popup.offsetHeight - margin)
    popup.style.left = `${x}px`
    popup.style.top = `${y}px`
  }

  hidePopup() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden")
  }

  popupOpen() {
    return this.hasPopupTarget && !this.popupTarget.classList.contains("hidden")
  }

  onDocClick(event) {
    if (this.popupOpen() && !this.popupTarget.contains(event.target)) this.hidePopup()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.hidePopup()
  }

  // Actions common to EVERY row in the set (the spec's intersection), in the
  // configured order.
  intersectActions(rows) {
    if (rows.length === 0) return []
    const sets = rows.map(r => new Set(this.rowActions(r)))
    const singleOnly = new Set(this.singleOnlyActionsValue)
    return this.actionOrderValue.filter(key => {
      if (singleOnly.has(key) && rows.length > 1) return false
      return sets.every(s => s.has(key))
    })
  }

  rowActions(row) {
    return (row.dataset.actions || "").split(",").map(s => s.trim()).filter(Boolean)
  }

  renderPopupItems(actions, targetRows) {
    const container = this.popupItemsTarget
    container.innerHTML = ""
    container.classList.toggle("hidden", actions.length === 0)
    if (actions.length === 0) return

    const ids = targetRows.map(r => r.dataset.rowId).filter(Boolean)

    for (const key of actions) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"

      const iconSlot = document.createElement("span")
      iconSlot.className = "inline-flex items-center justify-center w-4 h-4 shrink-0 text-zinc-500 dark:text-zinc-400"
      const tpl = this.element.querySelector(`template[data-action-icon="${key}"]`)
      if (tpl) iconSlot.appendChild(tpl.content.cloneNode(true))

      const labelSpan = document.createElement("span")
      labelSpan.textContent = this.actionLabelsValue[key] || key

      btn.appendChild(iconSlot)
      btn.appendChild(labelSpan)
      const submenu = this.actionSubmenusValue[key]
      const confirmMsg = this.actionConfirmsValue[key]
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        if (submenu) this.openSubmenu(key, submenu, ids)
        else if (confirmMsg) this.openConfirm(key, ids, confirmMsg)
        else this.submitAction(key, ids)
      })
      container.appendChild(btn)
    }
  }

  // ---------- destructive-action confirm dialog ----------

  // Swap the popup for a branded confirm dialog (mirrors the detail screen's
  // Void dialog) instead of a native window.confirm. The chosen action is held
  // pending until the dialog's confirm button POSTs it.
  openConfirm(key, ids, message) {
    this.pendingKey = key
    this.pendingIds = ids
    this.hidePopup()
    // `ids` are the rows that actually carry this action (the popup only offers
    // an action common to every targeted row), so their count is the
    // policy-eligible number that will be affected — interpolate it into the copy.
    this.confirmMessageTarget.textContent = message.replace("{count}", this.formatNumber(ids.length))
    if (this.hasConfirmLabelTarget) {
      this.confirmLabelTarget.textContent = this.actionConfirmLabelsValue[key] || this.confirmFallbackLabelValue
    }
    this.confirmDialogTarget.hidden = false
  }

  confirmAction() {
    const key = this.pendingKey
    const ids = this.pendingIds || []
    this.closeConfirm()
    if (key) this.submitAction(key, ids)
  }

  closeConfirm() {
    if (this.hasConfirmDialogTarget) this.confirmDialogTarget.hidden = true
    this.pendingKey = null
    this.pendingIds = null
  }

  // Close only when the centering wrapper itself is clicked (outside the card).
  closeConfirmOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closeConfirm()
  }

  // ---------- action submenu (e.g. "Snooze until…") ----------

  openSubmenu(key, cfg, ids) {
    if (this.hasPopupHeadingTarget) this.popupHeadingTarget.classList.add("hidden")
    const container = this.popupItemsTarget
    container.classList.remove("hidden")
    container.innerHTML = ""
    container.appendChild(this.submenuHeader(cfg))
    cfg.options.forEach(opt => {
      container.appendChild(this.submenuButton(opt.label, () => {
        this.submitAction(key, ids, { duration: opt.key })
      }))
    })
    container.appendChild(this.submenuButton(cfg.custom, () => this.renderSubmenuCustom(key, cfg, ids), true))
  }

  renderSubmenuCustom(key, cfg, ids) {
    const container = this.popupItemsTarget
    container.innerHTML = ""
    container.appendChild(this.submenuHeader(cfg))

    const wrap = document.createElement("div")
    wrap.className = "flex flex-col gap-2 px-3 py-2"
    const input = document.createElement("input")
    input.type = "date"
    input.className = "rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
    const confirm = document.createElement("button")
    confirm.type = "button"
    confirm.className = "rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
    confirm.textContent = cfg.confirm
    confirm.addEventListener("click", (e) => {
      e.stopPropagation()
      if (!input.value) return
      this.submitAction(key, ids, { until: input.value })
    })
    wrap.appendChild(input)
    wrap.appendChild(confirm)
    container.appendChild(wrap)
  }

  submenuHeader(cfg) {
    const header = document.createElement("div")
    header.className = "flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800"
    const back = document.createElement("button")
    back.type = "button"
    back.className = "inline-flex items-center justify-center w-4 h-4 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
    back.setAttribute("aria-label", cfg.back)
    const tpl = this.element.querySelector('template[data-action-icon="arrow_left"]')
    if (tpl) back.appendChild(tpl.content.cloneNode(true))
    else back.textContent = "‹"
    back.addEventListener("click", (e) => {
      e.stopPropagation()
      this.renderPopupItems(this.menuActions, this.menuRows)
      this.setPopupHeading(this.menuCount, this.menuActions.length)
    })
    const label = document.createElement("span")
    label.textContent = cfg.header
    header.appendChild(back)
    header.appendChild(label)
    return header
  }

  submenuButton(text, onClick, separated = false) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "flex items-center w-full text-left px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
    if (separated) btn.className += " border-t border-zinc-100 dark:border-zinc-800"
    btn.textContent = text
    btn.addEventListener("click", (e) => { e.stopPropagation(); onClick() })
    return btn
  }

  setPopupHeading(count, actionCount) {
    const heading = this.popupHeadingTarget
    const empty = actionCount === 0
    let text = ""
    if (count === 1) {
      if (empty) text = this.emptyHeadingValue
    } else {
      text = empty ? this.emptySelectionHeadingValue : `${this.formatNumber(count)} ${this.nounValue || "items"}`
    }

    const borderClasses = ["border-t", "border-zinc-100", "dark:border-zinc-800"]
    const errorClasses = ["text-rose-600", "dark:text-rose-400"]
    const normalClasses = ["text-zinc-500", "dark:text-zinc-400", "uppercase", "tracking-wide"]

    if (text === "") {
      heading.classList.add("hidden")
      heading.textContent = ""
      this.popupItemsTarget.classList.remove(...borderClasses)
      return
    }
    heading.classList.remove("hidden")
    heading.textContent = text
    this.popupItemsTarget.classList.add(...borderClasses)
    heading.classList.remove(...(empty ? normalClasses : errorClasses))
    heading.classList.add(...(empty ? errorClasses : normalClasses))
  }

  // POST the chosen action's endpoint with ids[] via a Turbo-handled form, so
  // the server redirect reloads the index. Closes the popup first.
  submitAction(key, ids, extra = {}) {
    const url = this.actionEndpointsValue[key]
    this.hidePopup()
    if (!url || ids.length === 0) return

    const form = document.createElement("form")
    form.method = "post"
    form.action = url
    form.style.display = "none"

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) form.appendChild(this.hidden("authenticity_token", token))
    ids.forEach(id => form.appendChild(this.hidden("ids[]", id)))
    Object.entries(extra).forEach(([name, value]) => form.appendChild(this.hidden(name, value)))

    document.body.appendChild(form)
    if (form.requestSubmit) form.requestSubmit()
    else form.submit()
  }

  hidden(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }
}
